<template>
  <div id="app">
    <!-- Show loading while checking auth (prevents login page flash during OAuth callback) -->
    <div v-if="authChecking" class="app-auth-loading">
      <p>Authenticating…</p>
    </div>
    <AuthLogin v-else-if="!isAuthenticated" @authenticated="handleAuthenticated" />
    <div v-else>
      <div class="app-layout">
        <AppSidebar
          :entities="sortedEntities"
          :current-view="currentView"
          :can-access-import="canAccessImport"
          :can-access-wizard="canAccessWizard"
          :can-access-onboarding="canAccessOnboarding"
          :can-access-mapping-validation="canAccessMappingValidation"
          @navigate="handleNavigate"
        />
        <div class="app-layout__main">
          <AppTopBar
            :app-title="appTitle"
            :user="user"
            :user-groups="userGroups"
            :current-view="currentView"
            :previous-view="previousView"
            :compare-environment="compareEnvironment"
            :can-compare="canCompare"
            :is-mobile="isMobile"
            @sign-out="signOut"
            @compare-change="handleTopBarCompareChange"
            @close-comparison="closeComparisonView"
            @toggle-sidebar="sidebarState.toggle()"
          />
          <div v-if="showHeaderFilters" class="app-layout__filters">
            <EntityFilters
              :entity-name="currentView"
              :show-filters="true"
              :service-options="headerFilterData.serviceOptions || []"
              :product-options="headerFilterData.productOptions || []"
              :pscu-client-options="headerFilterData.pscuClientOptions || []"
              :service-filter="headerFilterData.serviceFilter || ''"
              :product-filter="headerFilterData.productFilter || ''"
              :pscu-client-filter="headerFilterData.pscuClientFilter || ''"
              :target-service-filter="headerFilterData.targetServiceFilter || ''"
              :source-service-filter="headerFilterData.sourceServiceFilter || ''"
              :entity-config="currentEntityConfig"
              @service-change="handleHeaderServiceChange"
              @product-change="handleHeaderProductChange"
              @pscu-client-change="handleHeaderPscuClientChange"
              @target-service-change="handleHeaderTargetServiceChange"
              @source-service-change="handleHeaderSourceServiceChange"
            />
          </div>
          <main class="app-layout__content">
            <router-view v-slot="{ Component, route }">
              <component
                :is="Component"
                ref="entityManagerRef"
                @entityCountChanged="handleEntityCountChanged"
                @selectedCountChanged="handleSelectedCountChanged"
                @primaryDataChanged="handlePrimaryDataChanged"
                @filterDataChanged="handleFilterDataChanged"
                :readonly="isReadonly"
                :canDelete="canDelete"
                :service-filter="headerFilterData.serviceFilter"
                :product-filter="headerFilterData.productFilter"
                :target-service-filter="headerFilterData.targetServiceFilter"
                :source-service-filter="headerFilterData.sourceServiceFilter"
                v-bind="route.name === 'environment-comparison' ? {
                  primaryEnvironment: environmentStore.currentEnvironment,
                  compareEnvironment: environmentStore.compareEnvironment,
                  selectedEntity: route.params.entityName,
                  entityConfig: getEntityConfig(route.params.entityName as string),
                  primaryData: primaryEntityData
                } : {}"
              />
            </router-view>
          </main>
        </div>
      </div>
      <ToastContainer />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useEnvironmentStore } from './stores/environment';
import { useAuthStore } from './stores/auth';
import AuthLogin from './components/AuthLogin.vue';
import AppSidebar from './components/shared/AppSidebar.vue';
import AppTopBar from './components/shared/AppTopBar.vue';
import { useSidebarState } from './composables/useSidebarState';
import { useAuth } from './composables/useAuth';
import { getCurrentUser, signOut as cognitoSignOut } from 'aws-amplify/auth';
import { canAccessEnvironment, getEnabledEntities } from './utils/environmentConfig';

// Initialize router and stores
const router = useRouter();
const environmentStore = useEnvironmentStore();
const authStore = useAuthStore();

// Sidebar state
const sidebarState = useSidebarState();
const isMobile = sidebarState.isMobile;

// Authentication state
const isAuthenticated = ref(false);
const authChecking = ref(true);
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

const checkAuthState = async () => {
  authChecking.value = true;
  try {
    await getCurrentUser();
    isAuthenticated.value = true;
    authStore.isAuthenticated = true;
    startInactivityTimer();
  } catch {
    // Check if we're returning from OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('code') || urlParams.has('state')) {

      // Wait longer for OAuth callback processing
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Clear URL parameters and any intermediate paths (e.g. /loading) after OAuth callback
      window.history.replaceState({}, document.title, window.location.origin + '/');
      // Try to get user again after OAuth callback
      try {
        await getCurrentUser();
        isAuthenticated.value = true;
        authStore.isAuthenticated = true;
        startInactivityTimer();

      } catch (error) {

        isAuthenticated.value = false;
        authStore.isAuthenticated = false;
      }
    } else {
      isAuthenticated.value = false;
      authStore.isAuthenticated = false;
    }
  } finally {
    authChecking.value = false;
  }
};

const startInactivityTimer = () => {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  inactivityTimer = setTimeout(async () => {
    await signOut();
  }, 30 * 60 * 1000); // 30 minutes
};

const resetInactivityTimer = () => {
  if (isAuthenticated.value) {
    startInactivityTimer();
  }
};

const handleAuthenticated = () => {
  isAuthenticated.value = true;
  authStore.isAuthenticated = true;
  
  // Initialize entities after authentication
  initializeEntities();
  
  loadUserInfo();
  startInactivityTimer();
  
  // Always go to home screen on fresh login
  currentView.value = 'home';
  localStorage.setItem('currentView', 'home');
};

const signOut = async () => {
  try {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    await cognitoSignOut({ global: true });
    isAuthenticated.value = false;
    authStore.isAuthenticated = false;
    user.value = null;
    userGroups.value = [];
  } catch (err) {
    // Handle sign out error silently
  }
};

// User info refs
const user = ref<{ email: string; username: string } | null>(null);
const userGroups = ref<string[]>([]);

// Load user info
const loadUserInfo = async () => {
  try {
    const { extractSamlUserInfo } = await import('./utils/samlUserFix');
    
    // Extract user info from tokens only
    const userInfo = await extractSamlUserInfo();

    
    user.value = { 
      email: userInfo.email, 
      username: userInfo.name 
    };
    
    userGroups.value = userInfo.groups;
    
    // Update the auth store
    authStore.user = {
      email: userInfo.email,
      username: userInfo.name
    };
    authStore.userGroups = userInfo.groups as any[];
    
    // Update the global auth state
    const { useAuth } = await import('./composables/useAuth');
    const { setUserGroups } = useAuth();
    setUserGroups(userGroups.value);
    
  } catch (err) {

    // Fallback to basic info
    user.value = { email: 'Error loading user', username: 'Error' };
    userGroups.value = [];
  }
};
import EntityFilters from './components/shared/EntityFilters.vue';
import ToastContainer from './components/shared/ToastContainer.vue';

import { getClient } from './client';

// Use auth composable for permission checking
const { isAdmin, isDeployment, isDeveloper, isReadonly, canEdit, canDelete, userGroups: filteredUserGroups } = useAuth();



const currentEnvironment = computed(() => environmentStore.currentEnvironment);
const compareEnvironment = computed({
  get: () => environmentStore.compareEnvironment,
  set: (value: string) => {
    if (value) {
      environmentStore.setCompareEnvironment(value as any);
    } else {
      environmentStore.clearCompareEnvironment();
    }
  }
});

// Watch for environment changes
watch(() => environmentStore.currentEnvironment, (newEnv) => {
  if (newEnv) {
    // Environment changed via store
  }
}, { immediate: true });

// Watch for compare environment changes
watch(() => environmentStore.compareEnvironment, (newEnv) => {
  // Compare environment changed via store
}, { immediate: true });

// Listen for environment change events
const handleEnvironmentChange = (event: Event) => {
  const customEvent = event as CustomEvent<{ environment: string }>;
  const newEnv = customEvent.detail.environment;
  
  // Check if user can access this environment
  if (!canAccessEnvironment(newEnv, userGroups.value)) {
    return;
  }
  
  // Check if user has access to current screen in new environment
  const currentRoute = router.currentRoute.value;
  if (currentRoute.name === 'service-import' && !canAccessImport.value) {
    router.push({ name: 'home' });
  }
};

window.addEventListener('environmentChanged', handleEnvironmentChange);

const canAccessImport = computed(() => {
  const env = currentEnvironment.value;
  
  // Admin and deployment can access import in all environments
  if (isAdmin.value || isDeployment.value) return true;
  
  // Developer can access import only in dev and test
  if (isDeveloper.value && (env === 'dev' || env === 'test')) return true;
  
  // Readonly cannot access import
  return false;
});

const canAccessWizard = computed(() => {
  return isDeveloper.value || isAdmin.value;
});

const canAccessMappingValidation = computed(() => {
  return isDeveloper.value || isAdmin.value;
});

const canAccessOnboarding = computed(() => {
  return isDeveloper.value || isAdmin.value;
});

const canCompare = computed(() => {
  return currentView.value !== 'home' && currentView.value !== 'import' && currentView.value !== 'compare' && currentView.value !== 'wizard' && currentView.value !== 'function-test' && currentView.value !== 'mapping-validation' && currentView.value !== 'onboarding-copy';
});

const appTitle = computed(() => {
  return import.meta.env.VITE_APP_TITLE || 'Hazel Mapping Editor';
});

// Initialize client lazily to ensure Amplify is configured first
let client: any = null;
const getClientInstance = () => {
  if (!client) {
    client = getClient();
  }
  return client;
};



const currentView = ref('home');
const previousView = ref('');
const entityManagerRef = ref<any>(null);

const getEntityConfig = (entityName: string) => {
  if (!entities.value || entities.value.length === 0) {
    return null;
  }
  return entities.value.find(entity => entity.name === entityName);
};

const initializeEntities = () => {
  try {
    const clientInstance = getClientInstance();
    entities.value = getEntityConfigs(clientInstance);
  } catch (error) {
    entities.value = [];
  }
};

const entityCount = ref(0);
const selectedCount = ref(0);
const primaryEntityData = ref<any[]>([]);
const headerFilterData = ref({
  serviceOptions: [] as any[],
  productOptions: [] as any[],
  pscuClientOptions: [] as any[],
  serviceFilter: '',
  productFilter: '',
  pscuClientFilter: '',
  targetServiceFilter: '',
  sourceServiceFilter: ''
});

import { getEntityConfigs } from './config/entityConfig';
import type { EntityConfigWithCrud } from './config/entityConfig';

const entities = ref<EntityConfigWithCrud[]>([]);

const sortedEntities = computed(() => {
  const enabledEntities = getEnabledEntities(entities.value);
  return [...enabledEntities].sort((a: any, b: any) => a.name.localeCompare(b.name));
});


// Task 8.1: Computed to determine if header filters are visible
const showHeaderFilters = computed(() => {
  return !!(currentEntityConfig.value?.hasFilters && currentView.value !== 'home' && currentView.value !== 'import' && currentView.value !== 'compare' && currentView.value !== 'wizard' && currentView.value !== 'function-test' && currentView.value !== 'mapping-validation' && currentView.value !== 'onboarding-copy');
});




// Sync currentView with router
watch(() => router.currentRoute.value, (route) => {
  if (route.name === 'home') {
    currentView.value = 'home';
  } else if (route.name === 'service-import') {
    currentView.value = 'import';
  } else if (route.name === 'mapping-wizard') {
    currentView.value = 'wizard';
  } else if (route.name === 'mapping-validation') {
    currentView.value = 'mapping-validation';
  } else if (route.name === 'function-test') {
    currentView.value = 'function-test';
  } else if (route.name === 'product-onboarding-copy') {
    currentView.value = 'onboarding-copy';
  } else if (route.name === 'entity-manager' && route.params.entityName) {
    currentView.value = route.params.entityName as string;
  } else if (route.name === 'environment-comparison' && route.params.entityName) {
    currentView.value = 'compare';
    previousView.value = route.params.entityName as string;
  }
}, { immediate: true });

// Watch currentView to track entity screens
watch(currentView, (newView, oldView) => {
  if (oldView && oldView !== 'home' && oldView !== 'import' && oldView !== 'compare') {
    previousView.value = oldView;
  }
  
  // If leaving the comparison screen, reset the Compare dropdown
  if (oldView === 'compare' && newView !== 'compare') {
    environmentStore.clearCompareEnvironment();
    localStorage.removeItem('previousView');
  }
  
  // Reset all filter state when switching screens
  // Each entity loads its own options and should start with a clean filter state
  if (newView !== oldView) {
    headerFilterData.value = {
      serviceOptions: [],
      productOptions: [],
      pscuClientOptions: [],
      serviceFilter: '',
      productFilter: '',
      pscuClientFilter: '',
      targetServiceFilter: '',
      sourceServiceFilter: ''
    };
  }
});


const closeComparisonView = () => {
  environmentStore.clearCompareEnvironment();
  localStorage.removeItem('previousView');
  const targetView = previousView.value || 'home';
  if (targetView === 'home') {
    router.push({ name: 'home' });
  } else {
    router.push({ name: 'entity-manager', params: { entityName: targetView } });
  }
};

const handleNavigate = (view: string) => {
  window.scrollTo(0, 0);
};

const handleTopBarCompareChange = (env: string) => {
  if (env) {
    environmentStore.setCompareEnvironment(env as any);
  } else {
    environmentStore.clearCompareEnvironment();
  }
  
  if (env && currentView.value !== 'home' && currentView.value !== 'import' && currentView.value !== 'compare') {
    previousView.value = currentView.value;
    localStorage.setItem('previousView', previousView.value);
    router.push({ name: 'environment-comparison', params: { entityName: previousView.value } });
  } else if (!env && currentView.value === 'compare') {
    // User cleared compare selection while on compare view — navigate back
    closeComparisonView();
  }
};

const handleFilterDataChanged = (filterData: any) => {
  // Only update options arrays from EntityManager emissions.
  // Filter VALUES (serviceFilter, productFilter, pscuClientFilter, etc.)
  // are managed exclusively by the handleHeader*Change handlers to prevent
  // EntityManager's stale refs from overwriting user-selected filter values.
  if (filterData.serviceOptions !== undefined) {
    headerFilterData.value.serviceOptions = filterData.serviceOptions;
  }
  if (filterData.productOptions !== undefined) {
    headerFilterData.value.productOptions = filterData.productOptions;
  }
  if (filterData.pscuClientOptions !== undefined) {
    headerFilterData.value.pscuClientOptions = filterData.pscuClientOptions;
  }
};

const handleEntityCountChanged = (count: number) => {
  entityCount.value = count;
};

const handleSelectedCountChanged = (count: number) => {
  selectedCount.value = count;
};

const handlePrimaryDataChanged = (data: any) => {
  primaryEntityData.value = data || [];
};

const handleHeaderServiceChange = (serviceId: string) => {
  headerFilterData.value.serviceFilter = serviceId;
  if (entityManagerRef.value) {
    entityManagerRef.value.selectedServiceFilter = serviceId;
    entityManagerRef.value.filterByService?.();
  }
};

const handleHeaderProductChange = (productId: string) => {
  headerFilterData.value.productFilter = productId;
  if (entityManagerRef.value) {
    entityManagerRef.value.selectedProductFilter = productId;
    entityManagerRef.value.filterByProduct?.();
  }
};

const handleHeaderPscuClientChange = (clientId: string) => {
  headerFilterData.value.pscuClientFilter = clientId;
  if (entityManagerRef.value) {
    entityManagerRef.value.selectedPscuClientFilter = clientId;
    entityManagerRef.value.filterByPscuClient?.();
  }
};

const handleHeaderTargetServiceChange = (serviceId: string) => {
  headerFilterData.value.targetServiceFilter = serviceId;
  if (entityManagerRef.value) {
    entityManagerRef.value.selectedTargetServiceFilter = serviceId;
    entityManagerRef.value.filterByTargetService?.();
  }
};

const handleHeaderSourceServiceChange = (serviceId: string) => {
  headerFilterData.value.sourceServiceFilter = serviceId;
  if (entityManagerRef.value) {
    entityManagerRef.value.selectedSourceServiceFilter = serviceId;
    entityManagerRef.value.filterBySourceService?.();
  }
};

const refreshEntities = () => {
  if (entityManagerRef.value) {
    entityManagerRef.value.loadEntities();
  }
};

const showCreateModal = () => {
  if (isReadonly.value) return;
  
  if (entityManagerRef.value) {
    entityManagerRef.value.showCreateModal = true;
  }
};

const deleteSelected = () => {
  if (selectedCount.value === 0 || !canDelete.value) return;
  
  if (entityManagerRef.value) {
    entityManagerRef.value.confirmBulkDelete();
  }
};

// Watch for environment changes and trigger refresh
const environmentChangeKey = ref(0);
watch(() => environmentStore.currentEnvironment, () => {
  environmentChangeKey.value++;
}, { immediate: false });

// Add environment reactivity to entity configs
const getReactiveEntityConfig = (entityName: string) => {
  if (!entities.value || entities.value.length === 0) {
    return null;
  }
  
  const baseConfig = entities.value.find(entity => entity.name === entityName);
  if (!baseConfig) return null;
  
  // Force reactivity by including environmentChangeKey
  environmentChangeKey.value; // This makes the computed reactive to environment changes
  return baseConfig;
};

const currentEntityConfig = computed(() => {
  return getReactiveEntityConfig(currentView.value);
});

// Safe computed properties for entity configs to prevent lexical declaration errors
const serviceParamMappingConfig = computed(() => getEntityConfig('SERVICE_PARAM_MAPPING'));
const redirectUrlConfig = computed(() => getEntityConfig('REDIRECT_URL'));
const stepServiceMappingConfig = computed(() => getEntityConfig('STEP_SERVICE_MAPPING'));
const serviceParamConfig = computed(() => getEntityConfig('SERVICE_PARAM'));
const serviceConfig = computed(() => getEntityConfig('SERVICE'));

onMounted(async () => {

  // Initialize sidebar state and media listener
  sidebarState.init();
  sidebarState.setupMediaListener();
  
  // Handle OAuth callback first
  const urlParams = new URLSearchParams(window.location.search);

  
  if (urlParams.has('code') || urlParams.has('state')) {

    // Wait a moment for Amplify to process the OAuth callback
    await new Promise(resolve => setTimeout(resolve, 2000));
  }
  

  await checkAuthState();

  
  if (isAuthenticated.value) {

    // User is already logged in (page refresh or OAuth callback) - preserve current state
    await loadUserInfo();

    

    const savedCompareEnv = localStorage.getItem('compareEnvironment');
    if (savedCompareEnv) {

      environmentStore.setCompareEnvironment(savedCompareEnv as any);
      const savedPreviousView = localStorage.getItem('previousView');
      if (savedPreviousView) {
        previousView.value = savedPreviousView;
        router.push({ name: 'environment-comparison', params: { entityName: savedPreviousView } });
      }
    } else {
      const savedView = localStorage.getItem('currentView');

      currentView.value = savedView || 'home';
    }
  } else {

    // User not logged in - will show login screen
    currentView.value = 'home';
    compareEnvironment.value = '';
    localStorage.setItem('currentView', 'home');
    localStorage.setItem('compareEnvironment', '');
  }
  

  // Initialize entities regardless of auth state for dropdown
  initializeEntities();

  

  // Add activity listeners to reset inactivity timer
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  events.forEach(event => {
    document.addEventListener(event, resetInactivityTimer, true);
  });

  

});

onUnmounted(() => {
  sidebarState.teardownMediaListener();
});


</script>

<style>
#app {
  font-family: Arial, sans-serif;
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.app-auth-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  color: var(--color-text-muted, #6c757d);
  font-size: var(--text-base, 16px);
}

/* ===== App Layout Grid ===== */
.app-layout {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: 1fr;
  min-height: 100vh;
  width: 100%;
}

.app-layout__main {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  overflow: hidden;
}

.app-layout__filters {
  padding: var(--space-2) var(--space-4);
  background: var(--color-surface-raised);
  border-bottom: 1px solid var(--border-color);
}

.app-layout__content {
  flex: 1;
  overflow: auto;
  padding: var(--space-4);
}

@media (max-width: 767px) {
  .app-layout {
    grid-template-columns: 1fr;
  }
}

/* ===== Existing styles preserved ===== */
.controls-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
}

.header-title {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
}

header {
  margin-bottom: var(--space-4);
}

header nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
}

select {
  padding: var(--space-2);
  font-size: var(--text-base);
}

main {
  background-color: var(--bg-color, #f9f9f9);
  width: 100%;
  height: 100%;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
}

/* Shared button styles */
.sticky-buttons {
  position: sticky;
  top: 0;
  background: white;
  z-index: 25;
  margin-bottom: var(--space-2);
  padding: var(--space-2) 0;
}

.btn-primary {
  background: var(--color-primary, #007bff);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  margin-right: var(--space-2);
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-success {
  background: var(--color-success, #28a745);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  margin-right: var(--space-2);
}

.btn-success:hover {
  background: #218838;
}

.btn-success:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.btn-danger {
  background: var(--color-danger, #dc3545);
  color: white;
  border: none;
  border-radius: var(--radius-sm);
  padding: var(--space-2) var(--space-3);
  cursor: pointer;
  margin-right: var(--space-2);
}

.btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.btn-danger:disabled {
  background: #6c757d;
  cursor: not-allowed;
}
</style>